import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { PageHeader } from "../components/PageHeader";
import { LegacyOpsBanner } from "../components/LegacyOpsBanner";
import { AuthRequired } from "../components/AuthRequired";
import { EmptyState } from "../components/EmptyState";
import {
  listAffiliateWithdrawals,
  type AffiliateWithdrawalRow,
  type AffiliateWithdrawalStatusFilter,
} from "../api/affiliatePayouts";
import { mapAffiliateError } from "../api/affiliatePayoutsErrors";

// Affiliate-withdrawal queue.
//
// Bound to GET /v2/admin/bff/affiliate/withdrawals. Browser carries
// the bearer session only — no HMAC, no service secret.
//
// User-facing identifiers we render: id, identityId (operator-scope),
// amount, currency, shortened destination, status, timestamps,
// txHash (when paid). We deliberately do NOT render buyer wallet /
// email / IP / userAgent / customer id — the BE redacts them before
// shipping the row, but we re-enforce here by typing the row narrow.

function shortenDestination(d: string | null): string {
  if (typeof d !== "string" || d.length === 0) return "—";
  if (d.startsWith("0x") && d.length === 42) {
    return `${d.slice(0, 6)}…${d.slice(-4)}`;
  }
  if (d.length <= 16) return d;
  return `${d.slice(0, 8)}…${d.slice(-4)}`;
}

function statusBadge(s: AffiliateWithdrawalRow["status"]) {
  switch (s) {
    case "queued":
      return { label: "Queued", color: "var(--accent, #2c7be5)" };
    case "approved":
      return { label: "Approved", color: "#7a3fc2" };
    case "paid":
      return { label: "Paid", color: "#0a9444" };
    case "rejected":
      return { label: "Rejected", color: "#d23f3f" };
  }
}

function ActionLinks({ row }: { row: AffiliateWithdrawalRow }) {
  const href = `/admin/affiliate/withdrawals/${encodeURIComponent(row.id)}`;
  const styleBase = {
    fontSize: 11,
    padding: "2px 6px",
    border: "1px solid var(--border, #ccc)",
    borderRadius: 3,
    textDecoration: "none" as const,
    color: "var(--fg, inherit)",
  };
  return (
    <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
      <Link to={href} style={styleBase}>
        View
      </Link>
      {row.status === "queued" && (
        <>
          <Link
            to={`${href}#approve`}
            style={{ ...styleBase, borderColor: "var(--accent, #2c7be5)", color: "var(--accent, #2c7be5)" }}
          >
            Approve
          </Link>
          <Link
            to={`${href}#reject`}
            style={{ ...styleBase, borderColor: "#d23f3f", color: "#d23f3f" }}
          >
            Reject
          </Link>
        </>
      )}
      {row.status === "approved" && (
        <>
          <Link
            to={`${href}#pay`}
            style={{ ...styleBase, borderColor: "#d23f3f", color: "#d23f3f", fontWeight: 600 }}
          >
            Pay
          </Link>
          <Link
            to={`${href}#reject`}
            style={{ ...styleBase, borderColor: "#d23f3f", color: "#d23f3f" }}
          >
            Reject
          </Link>
        </>
      )}
    </div>
  );
}

function AffiliateWithdrawalsInner() {
  const [rows, setRows] = useState<AffiliateWithdrawalRow[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] =
    useState<AffiliateWithdrawalStatusFilter>("queued");

  const load = useCallback(
    async (filter: AffiliateWithdrawalStatusFilter) => {
      setError(null);
      setLoading(true);
      try {
        const r = await listAffiliateWithdrawals({ status: filter });
        setRows(r.items);
      } catch (e) {
        setError(mapAffiliateError(e).message);
      } finally {
        setLoading(false);
      }
    },
    [],
  );

  useEffect(() => {
    void load(statusFilter);
  }, [load, statusFilter]);

  return (
    <div>
      <details style={{ margin: "8px 0" }}>
        <summary style={{ fontSize: 12, color: "var(--fg-dim)", cursor: "pointer" }}>
          Advanced — endpoint &amp; notes
        </summary>
        <p style={{ margin: "6px 0", fontSize: 12, color: "var(--fg-dim)" }}>
          Bound to <code>GET /v2/admin/bff/affiliate/withdrawals</code> — bearer
          session + trusted device. User FE only creates queued rows; operators
          advance them here.
        </p>
      </details>

      <div style={{ display: "flex", gap: 8, alignItems: "center", margin: "12px 0" }}>
        <label htmlFor="aw-status" style={{ fontSize: 12, color: "var(--fg-dim)" }}>
          Status:
        </label>
        <select
          id="aw-status"
          value={statusFilter}
          onChange={(e) =>
            setStatusFilter(e.target.value as AffiliateWithdrawalStatusFilter)
          }
          style={{ padding: "4px 8px", fontSize: 13 }}
        >
          <option value="queued">Queued</option>
          <option value="approved">Approved</option>
          <option value="paid">Paid</option>
          <option value="rejected">Rejected</option>
          <option value="all">All</option>
        </select>
        <button
          type="button"
          onClick={() => void load(statusFilter)}
          disabled={loading}
          style={{ padding: "4px 12px", fontSize: 13 }}
        >
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error && (
        <div
          role="alert"
          style={{
            padding: 12,
            border: "1px solid #d23f3f",
            background: "rgba(210, 63, 63, 0.06)",
            color: "#d23f3f",
            margin: "12px 0",
            fontSize: 13,
          }}
        >
          {error}
        </div>
      )}

      {rows && rows.length === 0 && (
        <EmptyState
          title={
            statusFilter === "queued"
              ? "No queued partner payout requests."
              : statusFilter === "all"
                ? "No payout requests yet."
                : `No ${statusFilter} payout requests.`
          }
          hint="Partner withdrawal requests created in the user app land here for operator review."
        />
      )}

      {rows && rows.length > 0 && (
        <div style={{ overflowX: "auto" }}>
          <table
            style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: 13,
              minWidth: 1100,
            }}
          >
            <thead>
              <tr style={{ borderBottom: "1px solid var(--border, #ccc)", textAlign: "left" }}>
                <th style={{ padding: "8px 6px" }}>id</th>
                <th style={{ padding: "8px 6px" }}>identityId</th>
                <th style={{ padding: "8px 6px" }}>Amount</th>
                <th style={{ padding: "8px 6px" }}>Destination</th>
                <th style={{ padding: "8px 6px" }}>Status</th>
                <th style={{ padding: "8px 6px" }}>Requested</th>
                <th style={{ padding: "8px 6px" }}>Approved</th>
                <th style={{ padding: "8px 6px" }}>Paid</th>
                <th style={{ padding: "8px 6px" }}>txHash</th>
                <th style={{ padding: "8px 6px" }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const badge = statusBadge(r.status);
                return (
                  <tr
                    key={r.id}
                    style={{ borderBottom: "1px solid var(--border-faint, #eee)" }}
                  >
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 12 }}>
                      <Link
                        to={`/admin/affiliate/withdrawals/${encodeURIComponent(r.id)}`}
                        style={{ color: "var(--accent, #2c7be5)" }}
                      >
                        {r.id}
                      </Link>
                    </td>
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>
                      {r.identityId}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      <div style={{ fontFamily: "monospace" }}>
                        {r.amountHumanReadable ?? r.amountMinor}{" "}
                        <span style={{ color: "var(--fg-dim)" }}>{r.currency}</span>
                      </div>
                      <div style={{ fontSize: 10, color: "var(--fg-dim)" }}>
                        {r.amountMinor} minor
                      </div>
                    </td>
                    <td
                      style={{
                        padding: "8px 6px",
                        fontFamily: "monospace",
                        fontSize: 11,
                      }}
                      title={r.destination ?? undefined}
                    >
                      {shortenDestination(r.destination)}
                    </td>
                    <td style={{ padding: "8px 6px", color: badge.color, fontWeight: 600 }}>
                      {badge.label}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      <code>{r.requestedAt}</code>
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      {r.approvedAt ? <code>{r.approvedAt}</code> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontSize: 11 }}>
                      {r.paidAt ? <code>{r.paidAt}</code> : "—"}
                    </td>
                    <td style={{ padding: "8px 6px", fontFamily: "monospace", fontSize: 11 }}>
                      {r.txHash ? (
                        <span title={r.txHash}>
                          {r.txHash.slice(0, 10)}…{r.txHash.slice(-6)}
                        </span>
                      ) : (
                        "—"
                      )}
                    </td>
                    <td style={{ padding: "8px 6px" }}>
                      <ActionLinks row={r} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export function AffiliateWithdrawals() {
  return (
    <div>
      <PageHeader
        title="Affiliate withdrawals"
        subtitle="Operator queue for affiliate-commission payout. Money-movement actions (approve / pay) require an approved multisign proposalId."
        method="GET"
        path="/v2/admin/bff/affiliate/withdrawals"
      />
      <LegacyOpsBanner what="This is the old on-chain affiliate bucket payout path." />
      <AuthRequired>
        <AffiliateWithdrawalsInner />
      </AuthRequired>
    </div>
  );
}
